import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CustomModal from '../../CustomModal'
import ModalButton from '../../ButtonsModal/ModalButton'
import KeyBindings from '../../../keybindings'
import Button from '../../../buttons/Button'
import { FormButtons } from '../../../form/index.styled'
import TextAreaInputControlled from '../../../inputs/TextAreaInput/controlled'
import InfoMessage from '../../../message/InfoMessage'
import { emailPattern } from '../../../../../helper/email'
import { selectIsNeededZIndex } from '../../../../../redux/selector/system'
import { setCancelReasonOptions } from '../../../../../redux/reducer/all-order/constants'
import { useOrderInvoice } from '../../../table/InvoicesTable/hooks/useOrderInvoice'
import { getInvoiceViewStatus, INVOICE_STATUS } from '../../../table/InvoicesTable/constants'
import OrderModalDescription from '../OrderModalDescription'
import { CancelInvoiceOrderInputWrapper, CancelInvoiceOrderReasonsWrapper } from './index.styled'

const EVENT_Z_INDEX = 127
const OTHER_REASON = 'OTHER'
const OTHER_REASON_MAX_LENGTH = 60

const CancelInvoiceOrderModal = React.memo(({ isOpen = false, close, onConfirm, order }) => {
	const { t } = useTranslation()
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const inputRef = useRef(null)
	const [activeReasonIndex, setActiveReasonIndex] = useState(0)
	const [otherReason, setOtherReason] = useState('')
	const { invoice } = useOrderInvoice(order?.orderId, isOpen)
	const isInvoicePaid = !!invoice && getInvoiceViewStatus(invoice) === INVOICE_STATUS.PAID
	const isInvoiceEmailInvalid = !!invoice && !emailPattern.test(invoice.customer?.email ?? '')
	const reasons = useMemo(() => setCancelReasonOptions(), [])
	const otherReasonIndex = reasons.findIndex(reason => reason.value === OTHER_REASON)
	const activeReason = reasons[activeReasonIndex]
	const buttons = useMemo(() => reasons.map(reason => ({ icon: 'cancel', value: reason.value, text: t(reason.label), show: true })), [reasons, t])

	useEffect(() => {
		if (isOpen) {
			setActiveReasonIndex(0)
			setOtherReason('')
		}
	}, [isOpen])

	const selectReason = useCallback(index => {
		setActiveReasonIndex(index)
		if (reasons[index].value === OTHER_REASON) {
			inputRef.current?.focus()
		}
	}, [reasons])

	const handleReasonClick = useCallback((event, button, index) => {
		event.preventDefault()
		selectReason(index)
	}, [selectReason])

	const handleArrowUp = useCallback(() => {
		selectReason(activeReasonIndex === 0 ? reasons.length - 1 : activeReasonIndex - 1)
	}, [selectReason, activeReasonIndex, reasons.length])

	const handleArrowDown = useCallback(() => {
		selectReason(activeReasonIndex === reasons.length - 1 ? 0 : activeReasonIndex + 1)
	}, [selectReason, activeReasonIndex, reasons.length])

	const handleOtherReasonChange = useCallback(event => {
		setOtherReason(event.target.value.slice(0, OTHER_REASON_MAX_LENGTH))
	}, [])

	const handleOtherReasonFocus = useCallback(() => {
		setActiveReasonIndex(otherReasonIndex)
	}, [otherReasonIndex])

	const handleConfirm = useCallback(() => {
		const reasonCode = activeReason.value
		const customReason = reasonCode === OTHER_REASON ? otherReason.trim() : ''
		onConfirm(customReason || reasonCode, reasonCode)
	}, [activeReason, otherReason, onConfirm])

	return (
		<>
			<CustomModal isOpen={isOpen}
									 close={close}
									 size="small"
									 title={t('order.modal.cancelInvoiceOrderModal.titleInvoice')}
									 id="cancel-invoice-order-modal"
									 zIndex={EVENT_Z_INDEX}
									 disableBackButton={true}
									 returnZIndex={0}>
				<OrderModalDescription title={t('order.modal.cancelInvoiceOrderModal.descriptionTitle')}
															 text={t(isInvoicePaid ? 'order.modal.cancelInvoiceOrderModal.descriptionPaid' : 'order.modal.cancelInvoiceOrderModal.descriptionUnpaid')}>
					{isInvoiceEmailInvalid &&
						<InfoMessage center={true} fullWidth={true}>{t('order.modal.cancelInvoiceOrderModal.emailWarning')}</InfoMessage>}
				</OrderModalDescription>
				<CancelInvoiceOrderReasonsWrapper>
					{buttons.map((button, index) =>
						<ModalButton key={button.value}
												 index={index}
												 tabIndex={index}
												 button={button}
												 onClick={handleReasonClick}
												 isActive={index === activeReasonIndex}
												 className={'thinBorder ' + (index === buttons.length - 1 ? 'noBottomRadius' : '')} />)}
				</CancelInvoiceOrderReasonsWrapper>
				<CancelInvoiceOrderInputWrapper>
					<TextAreaInputControlled name="reason"
																	 type="text"
																	 refInput={inputRef}
																	 onChange={handleOtherReasonChange}
																	 onFocus={handleOtherReasonFocus}
																	 value={otherReason}
																	 placeholder={t('order.modal.cancelInvoiceOrderModal.placeholder')}
																	 autoComplete="off"
																	 className="lf-border" />
				</CancelInvoiceOrderInputWrapper>
				<FormButtons>
					<Button icon="clear" keyButton="F3" text={t('buttons.controls.cancel')}
									zIndex={EVENT_Z_INDEX} onClick={close} className="back" />
					<Button color="green" icon="checkmark" keyButton="F2" text={t('buttons.controls.ok')}
									zIndex={EVENT_Z_INDEX} onClick={handleConfirm} testId="button-submit" />
				</FormButtons>
			</CustomModal>

			{isNeededZIndex &&
				<KeyBindings arrowUp={handleArrowUp}
										 arrowDown={handleArrowDown} />}
		</>
	)
})

CancelInvoiceOrderModal.displayName = 'CancelInvoiceOrderModal'
export default CancelInvoiceOrderModal
